/**
 *  Formulaire du modal ajout employé
 */
import React, { useEffect, useState } from "react";

import { useTranslation } from "react-i18next";
import { usePoste } from "@hooks/settings";
import useEmailEmployeDuplicationCheck from "@hooks/useEmailEmployeDuplicationCheck";
import { useSubmitEmployeModalForm } from "@hooks/useSubmitEmployeModalForm";
import { useForm, Controller } from "react-hook-form";

import ModalIndex from "@Modals/layout/index";
import DefaultDatePicker from "@components/arhDatePicker/DefaultDatePicker";
import FormSimple from "@components/formGroup/formSimple";
import InputWithAddOptions from "@components/input/InputWithAddOptions";
import ListboxEmploye from "@components/tailwindComponents/ListboxEmploye";
import ListboxSimple from "@components/tailwindComponents/ListboxSimple";
import ListboxGroupe from "@components/tailwindComponents/ListboxGroupe/index";
import { REGEX_EMAIL } from "@helpers/_constants";
import { useReduxSelector } from "@hooks/useReduxSelector";
import useGenderType from "@pages/shared/employeFormNew/PersonnalInformations/hooksGenderType";
import useEmployeeCreationAccess from "@hooks/useEmployeeCreationAccess";

const EmployeFormModal = ({ onClickClose, show }) => {
  const { t } = useTranslation("pages.employés.infoPers");
  const { control, handleSubmit, reset, setValue, getFieldState } = useForm();
  const creationAccess = useEmployeeCreationAccess({ show, setValue, getFieldState });
  const { isSaving, saveFormData, isFormError } = useSubmitEmployeModalForm();
  const { isPersonalEmailValid } = useEmailEmployeDuplicationCheck();

  const employers = useReduxSelector(({ commonData }) => commonData.employers);
  const emplacements = useReduxSelector(({ commonData }) => commonData.emplacements);
  const permissions = useReduxSelector(({ commonData }) => commonData.permissions);
  const groupes = useReduxSelector((state) => state.commonData?.groupes || []);
  const postes = useReduxSelector(({ commonData }) => commonData.postes);
  const { _insertPoste, _editPoste, _deletePoste } = usePoste();
  const [isWaiting, setIsWaiting] = useState(false);
  
  const { allGenderTypes: translaGenre } = useGenderType();

  useEffect(() => {
    if (!show) {
      reset();
    }
  }, [show]);

  useEffect(() => {
    if (!isSaving) setIsWaiting(false);
  }, [isSaving]);

  const onSubmit = async (data) => {
    setIsWaiting(true);
    saveFormData({
      data,
      close: onClickClose,
      callback: () => {
        reset();
      },
    });
  };
  const setEmptyField = (value) => {
    return value.trim() !== "";
  };

  return (
    <ModalIndex
      isOpen={show}
      title={t("Ajouter un employé")}
      onClickClose={onClickClose}
      isLoading={isWaiting || creationAccess.isLoading}
      buttonOptions={{
        click: handleSubmit(onSubmit, isFormError),
        color: "green",
        title: t("Créer le dossier"),
      }}
      buttonCancelOptions={{
        click: () => onClickClose(),
        color: "outlined",
        title: t("Annuler"),
      }}
    >
      <form className="arh-relative arh-mt-8" type="button">
        <Controller
          control={control}
          name="legalName"
          defaultValue=""
          rules={{ required: true }}
          render={({
            field: { onChange, onBlur, value },
            fieldState: { invalid },
          }) => (
            <FormSimple
              label={`${t("Nom")} *`}
              placeholder={t("Nom")}
              ParentForm="arh-mb-[0.938rem]"
              classLabel="arh-flex"
              onChange={onChange}
              onBlur={onBlur}
              value={value}
              invalid={invalid}
            />
          )}
        />

        <Controller
          control={control}
          name="legalFirstName"
          defaultValue=""
          rules={{ required: true, validate: setEmptyField }}
          render={({
            field: { onChange, onBlur, value },
            fieldState: { invalid },
          }) => (
            <FormSimple
              label={`${t("Prénom")} *`}
              placeholder={t("Prénom")}
              ParentForm="arh-mb-[0.938rem]"
              classLabel="arh-flex"
              onChange={onChange}
              onBlur={onBlur}
              value={value}
              invalid={invalid}
            />
          )}
        />

        <Controller
          control={control}
          name="personalEmail"
          defaultValue=""
          rules={{
            required: true,
            pattern: {
              value: REGEX_EMAIL,
              message: t("object_invalid", { object: "emails" }),
            },
            validate: {
              duplicated_mail: async (email) => {
                if (!isWaiting) {
                  setIsWaiting(true);
                  const isEmailValid = await isPersonalEmailValid(email, true);
                  setIsWaiting(false);
                  return isEmailValid;
                }
              },
            },
          }}
          render={({
            field: { onChange, onBlur, value },
            fieldState: { invalid },
          }) => (
            <FormSimple
              label={`${t("Courriel de correspondance")} *`}
              placeholder={t("Courriel de correspondance")}
              ParentForm="arh-mb-[0.938rem]"
              classLabel="arh-flex"
              onChange={(e) => onChange(e.target.value.toLowerCase())}
              onBlur={onBlur}
              value={value}
              invalid={invalid}
            />
          )}
        />

        <Controller
          control={control}
          name="legalGender"
          defaultValue=""
          rules={{ required: true }}
          render={({
            field: { onChange, value, name },
            fieldState: { invalid },
          }) => (
            <div className="arh-mb-[0.938rem] arh-w-full">
              <label
                className={`arh-mb-1 arh-flex arh-w-full arh-font-poppins arh-text-xs arh-font-medium ${!invalid ? "arh-text-black" : "arh-text-[#F60A20]"
                  }`}
              >
                {`${t("Genre légal")} *`}
              </label>
              <div className="dropdown_content_hour dropdown_hoursWorking filter_posteAvailable arh-w-full">
                <ListboxSimple
                  placeholder={t("Genre légal")}
                  name={name}
                  optionList={translaGenre}
                  selectedValue={value}
                  setSelectedValue={onChange}
                  invalid={invalid}
                  noRadio
                  withTooltip
                  showReset
                />
              </div>
            </div>
          )}
        />

        <Controller
          control={control}
          name="post"
          defaultValue=""
          rules={{ required: true }}
          render={({
            field: { onChange, value, name },
            fieldState: { invalid },
          }) => (
            <div className="arh-mb-[0.938rem] arh-w-full">
              <label
                className={`arh-mb-1 arh-flex arh-w-full arh-font-poppins arh-text-xs arh-font-medium ${!invalid ? "arh-text-black" : "arh-text-[#F60A20]"
                  }`}
              >
                {`${t("Poste")} *`}
              </label>
              <div className="dropdown_content_hour dropdown_hoursWorking filter_posteAvailable arh-w-full">
                <InputWithAddOptions
                  defaultText={t("Poste")}
                  optionsList={postes || []}
                  secondDefaultText={t("Nom poste")}
                  name={name}
                  value={value?.title}
                  optionListLabel="title"
                  onChange={onChange}
                  showAction={false}
                  addItem={_insertPoste}
                  editItem={_editPoste}
                  deleteItem={_deletePoste}
                  validateDropdown={invalid}
                  readOnly
                  showReset
                  withSearch
                />
              </div>
            </div>
          )}
        />

        <Controller
          control={control}
          name="emplacement"
          defaultValue={[]}
          rules={{ required: true }}
          render={({
            field: { onChange, value, name },
            fieldState: { invalid },
          }) => (
            <div className="arh-mb-[0.938rem] arh-w-full">
              <label
                className={`arh-mb-1 arh-flex arh-w-full arh-font-poppins arh-text-xs arh-font-medium ${!invalid ? "arh-text-black" : "arh-text-[#F60A20]"
                  }`}
              >
                {`${t("Emplacement")} *`}
              </label>
              <div className="dropdown_content_hour dropdown_hoursWorking filter_posteAvailable arh-w-full">
                <ListboxSimple
                  placeholder={t("Emplacement")}
                  name={name}
                  optionList={emplacements}
                  selectedValue={value}
                  setSelectedValue={onChange}
                  invalid={invalid}
                  multiple
                  showReset
                />
              </div>
            </div>
          )}
        />

        <Controller
          name="Groupes"
          control={control}
          defaultValue={[]}
          render={({ field: { value, onChange, name } }) => (
            <ListboxGroupe
              label={t("Groupes")}
              placeholder={t("Groupes")}
              name={name}
              optionList={groupes}
              selectedValue={value}
              setSelectedValue={onChange}
              color="blue"
              withSearch
              showReset
            />
          )}
        />

        <Controller
          control={control}
          name="permission"
          defaultValue=""
          rules={{ required: true }}
          render={({
            field: { onChange, value, name },
            fieldState: { invalid },
          }) => (
            <div className="arh-mb-[0.938rem] arh-w-full">
              <label
                className={`arh-mb-1 arh-mt-4 arh-flex arh-w-full arh-font-poppins arh-text-xs arh-font-medium ${!invalid ? "arh-text-black" : "arh-text-[#F60A20]"
                  }`}
              >
                {`${t("Permission")} *`}
              </label>
              <div className="dropdown_content_hour dropdown_hoursWorking filter_posteAvailable arh-w-full">
                <ListboxSimple
                  placeholder={t("Permission")}
                  name={name}
                  optionList={creationAccess.permissions.length ? creationAccess.permissions : permissions}
                  optionTextAttribut="role"
                  selectedValue={value}
                  setSelectedValue={onChange}
                  invalid={invalid}
                />
              </div>
            </div>
          )}
        />

        <Controller
          control={control}
          name="accessProfile"
          defaultValue={null}
          render={({ field: { value, onChange, name } }) => (
            <div className="arh-mb-[0.938rem]">
              <label className="arh-mb-1 arh-flex arh-font-poppins arh-text-xs">{t("Profil d'accès")}</label>
              <ListboxSimple name={name} placeholder={t("Profil d'accès")} optionList={creationAccess.accessProfiles} optionTextAttribut="title" selectedValue={value} setSelectedValue={onChange} noRadio showReset />
            </div>
          )}
        />
        <Controller
          control={control}
          name="hierarchical"
          defaultValue={[]}
          rules={{ required: true }}
          render={({
            field: { onChange, value, name },
            fieldState: { invalid },
          }) => (
            <div className="arh-mb-[0.938rem] arh-w-full">
              <label
                className={`arh-mb-1 arh-flex arh-w-full arh-font-poppins arh-text-xs arh-font-medium ${!invalid ? "arh-text-black" : "arh-text-[#F60A20]"
                  }`}
              >
                {`${t("Supérieur hiérarchique")} *`}
              </label>
              <div className="dropdown_content_hour dropdown_hoursWorking filter_posteAvailable arh-w-full">
                <ListboxEmploye
                  placeholder={t("Supérieur hiérarchique")}
                  name={name}
                  optionList={employers}
                  selectedValue={value}
                  setSelectedValue={onChange}
                  customClass="arh-ml-[0.925rem]"
                  invalid={invalid}
                  multiple={false}
                />
              </div>
            </div>
          )}
        />

        <div className="employerThemeDatePicker blackSvgColor calendar_with_btn datepicker_vacation arh-static arh-flex-[0.5_0.5_0%] last:arh-ml-[1.25rem]">
          <label className="arh-mb-1 arh-flex arh-w-full arh-font-poppins arh-text-xs arh-font-medium arh-text-black ">
            {t("Date d'embauche")}
          </label>
          <Controller
            control={control}
            name="hiringDate"
            defaultValue=""
            render={({ field: { onChange, value, name } }) => (
              <DefaultDatePicker
                className="datepickerDropdown GreenThemeSelect"
                name={name}
                placeholder={t("Date d'embauche")}
                dateValue={value}
                setDateValue={onChange}
                classPosition=" calendar_right arh-bottom-12 arh-w-full"
              />
            )}
          />
        </div>
        <div className="arh-w-full arh-text-left">
          <span className="arh-mb-1 arh-w-full arh-font-poppins arh-text-xs arh-font-light arh-italic arh-text-black">
            {t("Date nécéssaire pour utiliser la fonctionnalité d’onboarding")}
          </span>
        </div>
      </form>
    </ModalIndex>
  );
};

export default EmployeFormModal;
